import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getUiInterface } from "../../locales/translationUtils";
import { solicitarRedefinicaoSenha } from "../../services/userService";

interface ForgotPasswordModalProps {
  show: boolean;
  onHide: () => void;
}

/**
 * Formulário de "esqueci minha senha", acessado a partir da tela de login. A resposta
 * do back-end é sempre a mesma mensagem genérica, exista ou não o usuário/e-mail
 * informado - de propósito, ver RedefinicaoSenhaController.Solicitar - então a tela
 * também não distingue os dois casos.
 */
const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const [usuarioOuEmail, setUsuarioOuEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Limpa o estado ao fechar, pra nao reaparecer resquicio da tentativa anterior na
  // proxima vez que o usuario abrir o modal.
  const fechar = () => {
    setUsuarioOuEmail("");
    setMensagemSucesso(null);
    setErro(null);
    setEnviando(false);
    onHide();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    if (!usuarioOuEmail.trim()) {
      setErro(t("forgotPassword.required"));
      return;
    }

    setEnviando(true);
    try {
      const resposta = await solicitarRedefinicaoSenha({ usuarioOuEmail: usuarioOuEmail.trim() });
      setMensagemSucesso(resposta?.messages?.join(" ") || t("forgotPassword.success"));
    } catch (err: any) {
      const messages: string[] | undefined = err?.response?.data?.messages;
      setErro(messages?.length ? messages.join(" ") : t("forgotPassword.error"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal show={show} onHide={fechar} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("forgotPassword.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {mensagemSucesso ? (
          <div className="alert alert-success mb-0" role="alert">
            {mensagemSucesso}
          </div>
        ) : (
          <>
            <p className="text-muted" style={{ fontSize: "14px" }}>
              {t("forgotPassword.instructions")}
            </p>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formUsuarioOuEmail">
                <Form.Label>{t("forgotPassword.label")}</Form.Label>
                <Form.Control
                  type="text"
                  autoFocus
                  value={usuarioOuEmail}
                  onChange={(e) => setUsuarioOuEmail(e.target.value)}
                />
              </Form.Group>
              {erro && <div className="text-danger mt-2">{erro}</div>}
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={fechar}>
          {mensagemSucesso ? getUiInterface("close") : getUiInterface("cancel")}
        </Button>
        {!mensagemSucesso && (
          <Button variant="primary" onClick={handleSubmit} disabled={enviando}>
            {enviando ? <Spinner animation="border" size="sm" /> : t("forgotPassword.submit")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ForgotPasswordModal;
